import { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { json } from '@codemirror/lang-json'
import { keymap } from '@codemirror/view'
import { indentWithTab } from '@codemirror/commands'

// The app uses Tailwind darkMode: 'media', so match the editor theme to the OS
// preference like the surrounding dark: styles do.
function prefersDark() {
  return typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

/**
 * A small JSON code editor (CodeMirror 6) with line numbers, syntax
 * highlighting, bracket matching and tab-to-indent. Exposes an imperative
 * `insertText(text)` so a palette can drop tokens at the cursor.
 */
const JsonCodeEditor = forwardRef(function JsonCodeEditor(
  // maxHeight defaults to undefined so the editor grows to fit its content —
  // the surrounding modal then provides a single scroll region rather than the
  // editor trapping the scroll.
  { value, onChange, minHeight = '160px', maxHeight = undefined },
  ref
) {
  const cmRef = useRef(null);
  const [isDark, setIsDark] = useState(prefersDark());

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onChangeTheme = () => setIsDark(mq.matches);
    mq.addEventListener?.('change', onChangeTheme);
    return () => mq.removeEventListener?.('change', onChangeTheme);
  }, []);

  useImperativeHandle(ref, () => ({
    insertText(text) {
      const view = cmRef.current?.view;
      if (!view) {
        onChange((value || '') + text);
        return;
      }
      const { from, to } = view.state.selection.main;
      view.dispatch({
        changes: { from, to, insert: text },
        selection: { anchor: from + text.length },
      });
      view.focus();
    },
  }));

  return (
    <div className="rounded-md overflow-hidden ring-1 ring-inset ring-gray-300 dark:ring-zinc-600">
      <CodeMirror
        ref={cmRef}
        value={value}
        onChange={onChange}
        theme={isDark ? 'dark' : 'light'}
        extensions={[json(), keymap.of([indentWithTab])]}
        basicSetup={{
          lineNumbers: true,
          foldGutter: false,
          autocompletion: false,
          highlightActiveLine: true,
          bracketMatching: true,
          closeBrackets: true,
        }}
        minHeight={minHeight}
        maxHeight={maxHeight}
        className="text-xs"
      />
    </div>
  );
});

export default JsonCodeEditor;
